// useCustomNavigate.js
import { useNavigationHistory } from 'contexts/NavigationContext';
import { useNavigate, useLocation, Location } from 'react-router-dom';
import {z} from 'zod';

export function useHistoryNavigate() {
  const { pushToHistory } = useNavigationHistory();
  const navigate = useNavigate();
  const location = useLocation();

  // Return a function that first updates the navigation history, then navigates
  function navigateWithState(to, options: { state?: { from?: string } } = {}) {
    // curr location
    const currLocation = getFullLocationUrl(location);

    // Initialize a new state object from the state provided in options if it exists, or create a new object
    const newState = options.state ? { ...options.state } : {};

    // Check if 'from' property does not exist in the newState object and then set it
    if (typeof newState.from === 'undefined') {
      newState.from = getFullLocationUrl(location);
    }

    pushToHistory(currLocation);
    // Perform the navigation with the new state and any other options
    navigate(to, { ...options, state: newState });
  }

  return navigateWithState;
}

export const getFullLocationUrl = (location: Location) => {
  return `${location.pathname}${location.search}${location.hash}`;
}

/** `location.state` as set on the way in: `from` is the launching page. */
const LocationStateFromSchema = z.object({
  // An in-app path only: one leading slash, so never another origin.
  from: z.string().regex(/^\/(?!\/)/),
});

/**
 * The page that navigated here, from `location.state.from`. Links and
 * {@link useHistoryNavigate} set it to the launching page's URL so the
 * destination can offer a way back. It does not survive a reload or a new
 * tab.
 *
 * @param state - The router's `location.state`, of any shape.
 * @return The in-app URL to go back to, or undefined when no page set one or
 *   the value is not an in-app path.
 * @throws Never.
 */
export function getLocationStateFrom(state: unknown): string | undefined {
  const parsed = LocationStateFromSchema.safeParse(state);
  return parsed.success ? parsed.data.from : undefined;
}
